import { useContext, useState } from "react";
import SpeedDial from "@mui/material/SpeedDial";
import SpeedDialAction from "@mui/material/SpeedDialAction";
import { Avatar, Box } from "@mui/material";
import accountIcon from "../../assets/accountIcon.svg";
import styles from "../../content/pages/homepage.module.css";
import { WidgetContext } from "src/contexts/WidgetContext";
import PaymentMethodSwipeable from "../PaymentMethod/PaymentMethodSwipeable";
import LogOutButton from "../LogComponents/LogOutButton";
import DeleteAccountButton from "../DeleteAccountButton/DeleteAccountButton";
import { useDoLogout } from "src/utils/useDoLogout";
import DeleteAccountDialog from "../DeleteAccountButton/DeleteAccountDialog";
import { useAuth0 } from "@auth0/auth0-react";
import { deleteUser } from "src/api/deleteUser";
import { useSafeAccessAuth0Token } from "src/hooks/useSafeAccessAuth0Token";
import PaymentButton from "../PaymentMethod/PaymentButton";
import { PureLightThemeCaaS } from "src/theme/schemes/PureLightTheme";

export default function QuanAccountSpeedDial({
    onClose,
    isAiDialogOpen,
    onOpenAiSettings,
    onCloseAiSettings,
    onConfirmAiSettings,
    aiSettings,
    isUpdatingAiSettings,
    isPaymentOpen,
    onPaymentOpenChange,
    initialPaymentView,
    credits,
    enableCreditValidation,
    userSubscription,
}) {
    const [alert, setAlert] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [selectedIcon, setSelectedIcon] = useState<string | null>(null);
    const [isHelpDrawerOpen, setIsHelpDrawerOpen] = useState(false);
    const { openDrawer, isLoggedInAuth0ExperienceGlobal, freeCredits } = useContext(WidgetContext);

    const subscriptionIsFree = (userSubscription || "free").toLowerCase() === "free";
    const isOutOfCredits = subscriptionIsFree
      ? (freeCredits ?? 0) <= 9
      : (credits ?? 0) <= 9;

    //  Add state to control the delete dialog
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    // 2. Get the logout function
    const doLogOut = useDoLogout();
    //Get the access token from Auth0 using the safe hook
    const { getSafeAccessAuth0Token } = useSafeAccessAuth0Token();
    const { getAccessTokenSilently, user } = useAuth0();

    //Function to toggle the speed dial
    const handleSettingsClick = () => {
        setIsOpen(!isOpen);
    };

    //Function to toggle alert state
    const handleClickAlert = () => {
        setAlert(!alert);
    };
    //Functions to open/close settingIcon
    // const handleOpen = () => setIsOpen(true);
    // const handleClose = () => setIsOpen(false);

    // Function to handle click on CardId icon
    const handleCardIdClick = () => {
        openDrawer();
    };

    // Function to handle click on icon
    const handleIconClick = (iconId: string) => {
        setSelectedIcon(iconId);
    };

    // 3.   Function that is executed when the user confirms the deletion
    const handleConfirmDelete = async () => {
        setIsDeleting(true);
        try {
            // 1. Get the access token from Auth0
            // const accessTokenFromAuth0 = await getAccessTokenSilently();
            const accessTokenFromAuth0 = await getSafeAccessAuth0Token();

            // 2. Call the API function to delete the user
            const isDeleted = await deleteUser(accessTokenFromAuth0);

            // 3. If the deletion was successful, logout
            if (isDeleted) {
                doLogOut();
            }
        } catch (error) {
            console.error("Failed to get access token for deletion", error);
        } finally {
            // 4. Always close the dialog, even if there is an error
            setIsDeleting(false);
            setIsDeleteDialogOpen(false);
        }
    };

    // Single click on account icon opens Payment Method menu (no SpeedDial expansion)
    const handleAccountIconClick = () => {
        onCloseAiSettings();
        setIsDeleteDialogOpen(false);
        onPaymentOpenChange(true);
    };

    // console.log("user", user);
    return (
        <>
            {/* Account icon: Auth0 user picture or name initial; fallback to default icon. Click opens PaymentMethodMenu. */}
            <Box
                component="button"
                type="button"
                aria-label="Account settings"
                onClick={handleAccountIconClick}
                sx={{
                    width: 30,
                    height: 30,
                    minWidth: 30,
                    minHeight: 30,
                    p: 0,
                    border: `1px solid ${PureLightThemeCaaS.colors.alpha.gray[100]}`,
                    borderRadius: "20px",
                    backgroundColor: "black",
                    cursor: "pointer",
                    pointerEvents: "auto",
                    touchAction: "auto",
                    position: "relative",
                    zIndex: 14000,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                }}
            >
                {/* If the user has a picture, use the picture. If not, use the name initial. If no name, use the default icon. */}
                {user?.picture ? (
                    <Avatar
                        src={user.picture}
                        alt={user.name ?? "Account"}
                        sx={{ width: "100%", height: "100%", fontSize: "0.875rem" }}
                        slotProps={{ img: { referrerPolicy: "no-referrer" } }}
                    >
                        {user?.name?.trim().charAt(0).toUpperCase()}
                    </Avatar>
                ) : user?.name ? (
                    <Avatar sx={{ width: "100%", height: "100%", fontSize: "0.875rem", bgcolor: `${PureLightThemeCaaS.colors.alpha.orange[100]}`, color: "white" }}>
                        {user.name.trim().charAt(0).toUpperCase()}
                    </Avatar>
                ) : (
                    <img src={accountIcon} alt="Account" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                )}
            </Box>

            {/* PREVIOUS: SpeedDial that expanded with 3 actions (Card, Delete Account, Log Out). Kept commented for reference. */}
            {/* <SpeedDial
                ariaLabel="Settings Speed Dial"
                FabProps={{
                    sx: {
                        width: "30px !important",
                        height: "30px !important",
                        minWidth: "30px !important",
                        minHeight: "30px !important",
                    },
                }}
                sx={{
                    // width: "40px",
                    // height:  "40px",
                    p: 0,
                    bgcolor: "transparent",
                    flexDirection: "row",
                    pointerEvents: "auto",
                    position: "relative",
                    touchAction: "auto", // allow touch events
                    zIndex: 14000,
                    border: `1px solid ${PureLightThemeCaaS.colors.alpha.gray[100]}`,
                    borderBottom: isOpen ? "none" : `1px solid ${PureLightThemeCaaS.colors.alpha.gray[100]}`,

                    backgroundColor: "black", //this is the background color of the speed dial to avoid overlap with the text thoughts
                    borderTopLeftRadius: 20,
                    borderTopRightRadius: 20,

                    borderBottomLeftRadius: isOpen ? 0 : 20,
                    borderBottomRightRadius: isOpen ? 0 : 20,
                    "& .MuiSpeedDialAction-fab": {
                        width: "30px",
                        minWidth: "30px",
                        height: "30px",
                        minHeight: "30px",
                        // Override MUI's staggered delay (30ms per action) so icons and background appear as one block
                        transitionDelay: "0ms !important",
                    },
                    "& .MuiSpeedDial-actions": {
                        backgroundColor: isOpen ? "black" : "transparent", //this is the background color of the speed dial to avoid overlap with the text thoughts
                        borderBottomLeftRadius: 20,
                        borderBottomRightRadius: 20,
                        // Fade container in/out so background and icons are painted as one unit (avoids icons-first-then-background)
                        opacity: isOpen ? 1 : 0,
                        transition: "opacity 0.15s ease-out",
                        // Like AudioSound: real border left/right/bottom only (no top). box-sizing + width/left so no layout jump.
                        ...(isOpen && {
                            boxSizing: "border-box",
                            width: "32px",
                            left: "-1px",
                            borderLeft: `1px solid ${PureLightThemeCaaS.colors.alpha.gray[100]}`,
                            borderRight: `1px solid ${PureLightThemeCaaS.colors.alpha.gray[100]}`,
                            borderBottom: `1px solid ${PureLightThemeCaaS.colors.alpha.gray[100]}`,
                        }),
                        padding: "0px",
                        position: "absolute",
                        gap: 0,
                        // right: "100%",
                        top: "100%",
                        // left: "40%",
                        // transform: "translateX(-40%)",
                        marginTop: "0px",
                        paddingRight: "0",
                        paddingTop: "0px",
                        paddingBottom: "5px",
                        // paddingRight: "30px",
                        display: "flex",
                        // flexDirection: "row-reverse",
                        flexDirection: "column",
                        touchAction: "auto", // allow touch events in the actions
                        pointerEvents: "auto", // allow pointer events in the actions
                    },
                }}
                direction="down"
                onOpen={() => setIsOpen(true)} // This is the function that opens the speed dial whit hover
                onClose={() => setIsOpen(false)} // the same but for close
                onClick={handleSettingsClick}
                open={isOpen}
                icon={<img src={accountIcon} alt="Settings" />}
            >
                {isLoggedInAuth0ExperienceGlobal && (
                    <SpeedDialAction
                        onClick={() => {
                            handleIconClick("Card");
                            onCloseAiSettings();
                            setIsDeleteDialogOpen(false);
                            onPaymentOpenChange(true);
                        }}
                        key="Card"
                        icon={<PaymentButton />}
                        className={`${styles.iconsLombard} iconWrapper`}
                    />
                )}
                {isLoggedInAuth0ExperienceGlobal && (
                    <SpeedDialAction
                        key="deleteAccount"
                        icon={<DeleteAccountButton color={"#ffffff"} />}
                        className={`${styles.iconsLombard} iconWrapper`}
                        // tooltipTitle="Delete Account"
                        // 4. When clicked, open the dialog
                        onClick={() => {
                            onCloseAiSettings();
                            onClose();
                            setIsDeleteDialogOpen(true);
                        }}
                    />
                )}
                {isLoggedInAuth0ExperienceGlobal && (
                    <SpeedDialAction key="logout" icon={<LogOutButton color={"#ffffff"} />} className={`${styles.iconsLombard} iconWrapper`} />
                )}
            </SpeedDial> */}

            {/* Render the dialog and pass the functions */}
            <DeleteAccountDialog
                open={isDeleteDialogOpen}
                onClose={() => setIsDeleteDialogOpen(false)}
                onConfirm={handleConfirmDelete}
                isDeleting={isDeleting}
                onBack={() => {
                    setIsDeleteDialogOpen(false);
                    onPaymentOpenChange(true);
                }}
                onCancelSubscriptionClick={() => {
                    setIsDeleteDialogOpen(false);
                    onPaymentOpenChange(true, "cancelMembership");
                }}
            />

            {/* Render the payment method swipeable */}
            <PaymentMethodSwipeable
                onClose={onClose}
                isPaymentOpen={Boolean(isPaymentOpen)}
                onPaymentOpenChange={onPaymentOpenChange}
                initialView={initialPaymentView}
                isRequired={enableCreditValidation && isOutOfCredits && initialPaymentView === "manageMembership"}
                onDeleteAccountClick={() => {
                    onPaymentOpenChange(false);
                    setIsDeleteDialogOpen(true);
                }}
                userSubscription={userSubscription}
            />

        </>
    );
}
